import { BiUpdateDatasetRequest } from '@euler/model/bi/library';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useState } from 'react';
import { biErrorMessage } from '../../../functions/biErrors';

/** Renaming, describing and deleting an uploaded dataset (`manage`). */
export function useBiDatasetActions(datasetId: string, onChanged: () => void) {
  const { biService } = useServiceFactory();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const update = useCallback(
    async (request: BiUpdateDatasetRequest): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);
      try {
        await biService.updateDataset(datasetId, request);
        onChanged();
        return true;
      } catch (error) {
        console.error('BI dataset update failed', datasetId, error);
        setSaveError(biErrorMessage(error, '没有保存成功，请稍后重试。'));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [biService, datasetId, onChanged],
  );

  /** False when refused: the refusal names the dashboards still using it. */
  const remove = useCallback(async (): Promise<boolean> => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await biService.deleteDataset(datasetId);
      return true;
    } catch (error) {
      console.error('BI dataset delete failed', datasetId, error);
      setDeleteError(biErrorMessage(error, '没有删除成功，请稍后重试。'));
      // Its usage may have changed since the page read it.
      onChanged();
      return false;
    } finally {
      setDeleting(false);
    }
  }, [biService, datasetId, onChanged]);

  const clearSaveError = useCallback(() => setSaveError(null), []);
  return {
    saving,
    saveError,
    clearSaveError,
    update,
    deleting,
    deleteError,
    remove,
  };
}
